import z from 'zod';

import {
  VariableNodeSchema,
  TextNodeSchema,
  LinkNodeSchema,
  ParagraphNodeSchema,
  LinebreakNodeSchema,
  ListNodeSchema,
  ListItemNodeSchema,
  WithChildren,
  TabNodeSchema,
  LookupNodeSchema,
} from './base';

type RichTextNode =
  | z.infer<typeof VariableNodeSchema>
  | z.infer<typeof LookupNodeSchema>
  | z.infer<typeof TextNodeSchema>
  | z.infer<typeof LinebreakNodeSchema>
  | z.infer<typeof TabNodeSchema>
  | (z.infer<typeof LinkNodeSchema> & { children: RichTextNode[] })
  | (z.infer<typeof ParagraphNodeSchema> & { children: RichTextNode[] })
  | (z.infer<typeof ListNodeSchema> & { children: RichTextNode[] })
  | (z.infer<typeof ListItemNodeSchema> & { children: RichTextNode[] });

const RichTextElementSchema = z.discriminatedUnion('type', [
  WithChildren(ParagraphNodeSchema, (): z.ZodType<RichTextNode> => RichTextNodeV1Schema),
  WithChildren(ListNodeSchema, (): z.ZodType<RichTextNode> => RichTextNodeV1Schema),
]);
const RichTextNodeV1Schema = z.discriminatedUnion('type', [
  VariableNodeSchema,
  LookupNodeSchema,
  TextNodeSchema,
  TabNodeSchema,
  LinebreakNodeSchema,
  WithChildren(ListNodeSchema, (): z.ZodType<RichTextNode> => RichTextNodeV1Schema),
  WithChildren(LinkNodeSchema, (): z.ZodType<RichTextNode> => RichTextNodeV1Schema),
  WithChildren(ListItemNodeSchema, (): z.ZodType<RichTextNode> => RichTextNodeV1Schema),
]);

export const RichTextV1Schema = z.object({
  type: z.literal('root'),
  version: z.literal(1),
  children: z.array(RichTextElementSchema),
  format: z.enum(['left', 'start', 'center', 'right', 'end', 'justify', '']).optional(),
});

const RichTextElementGenerationSchema = z.discriminatedUnion('type', [
  WithChildren(ParagraphNodeSchema, (): z.ZodType<RichTextNode> => RichTextNodeGenerationV1Schema),
  WithChildren(ListNodeSchema, (): z.ZodType<RichTextNode> => RichTextNodeGenerationV1Schema),
]);

// Rich text generation does not support variables
const RichTextNodeGenerationV1Schema = z
  .discriminatedUnion('type', [
    TextNodeSchema,
    TabNodeSchema,
    LinebreakNodeSchema,
    WithChildren(ListNodeSchema, (): z.ZodType<RichTextNode> => RichTextNodeGenerationV1Schema),
    WithChildren(LinkNodeSchema, (): z.ZodType<RichTextNode> => RichTextNodeGenerationV1Schema),
    WithChildren(ListItemNodeSchema, (): z.ZodType<RichTextNode> => RichTextNodeGenerationV1Schema),
  ])
  .describe(
    "The node types for formatting with RichText. Allowed types are: 'link', 'paragraph', 'linebreak', 'list', and 'listitem'.",
  );

export const RichTextGenerationV1Schema = z
  .object({
    type: z.literal('root').describe('The root node of the rich text document.'),
    version: z.literal(1),
    children: z
      .array(RichTextElementGenerationSchema)
      .describe('The children of the root node, must be an array of correctly formatted rich text nodes.'),
    format: z.enum(['left', 'start', 'center', 'right', 'end', 'justify', '']).optional(),
  })
  .describe('A schema to represent content as HTML, the top level must be a root node.');
